import { pageTitle } from '@palaccount/config';
import { MFG_MESSAGES, type BomResponse } from '@palaccount/contracts';
import { GitBranch, History, Plus, ScanSearch, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { QuickCreateSelect } from '@/features/quick-create/quick-create-select';
import { localizeServerMessage, refusalMessage } from '@/lib/server-messages';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { formatQty } from '@/lib/format';

import { Typeahead } from '@/components/ui/typeahead';
import { ItemCell } from '../inventory/item-cell';
import { useItemPicker } from '../inventory/item-picker';
import { useUnits } from '../inventory/api';
import { Modal } from '../inventory/modal';
import { findLiveBom, useBom, useBomMutations, useFirstBom, useWhereUsed } from './api';
import { ManufacturingTabs } from './manufacturing-tabs';
import { trimQty, visibleWorkstation } from './mfg-format';

/**
 * MFG-01 — BILLS OF MATERIALS.
 *
 * "You cannot type a cost here. You can only type the recipe, and the money
 * follows" (§1). Every figure in the costing panel arrives from the shared
 * service (§9.4); nothing on this page computes one, and only the overhead
 * percentage is an input (BR-MFG-01-12).
 *
 * ONE SCREEN. *Changed by owner ruling R1: "The prototype drew two versions of
 * this screen — a materials-only parts list and a full engineering view — and
 * hid routing and version management behind the simplified one. There is one
 * screen."* Routing, version history, where-used and the full cost breakdown
 * are all here for anyone who may open the page; what varies between users is
 * what they may CHANGE, and that is a permission checked on the server.
 *
 * §4: "There are no action buttons in the header card — no Save, no New
 * version, no Duplicate, no Print, no overflow menu." Saving is the shell's
 * job; version creation happens by itself at activation (OQ-9.7-4).
 */
export function BomPage() {
  const { t } = useTranslation();
  const canEdit = useCan('manufacturing.edit');
  const canCreate = useCan('manufacturing.create');

  /*
   * ONE ROW, because one row is all this screen ever wanted (NFR-42).
   *
   * This used to ask for `MAX_PAGE_SIZE` — 500 recipes, each decoded with its
   * components AND its routing, because `toResponse` reads both per row — and
   * then used exactly one of them: `boms[0]`, as the opening selection.
   *
   * MFG-01 is an ITEM-keyed master/detail and draws no recipe list, so there
   * is nothing here for a `<Pager>` to page. The question that used to tie
   * this screen to an unbounded read — "does this finished item already have
   * a live recipe?" — is `findLiveBom`, one query about one item, asked at
   * the moment somebody picks it (see `onPickItem`).
   */
  const { data: firstBom, isPending } = useFirstBom();
  const { data: units } = useUnits();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: bom } = useBom(selectedId ?? undefined);
  const { create, update, setStatus, remove } = useBomMutations();

  const [tab, setTab] = useState<'components' | 'routing'>('components');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [whereUsedItem, setWhereUsedItem] = useState<{ id: string; name: string } | null>(null);
  const [activatePrompt, setActivatePrompt] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('mfg.bomTitle'));
  }, [t]);

  /*
   * GEN-50 — the finished-item picker offers every stocked PRODUCT, searched
   * rather than listed, because §15's "Empty" state is a finished item with
   * no recipe at all: choosing one opens a blank Draft v1. Whether it already
   * HAS one is asked of the server per pick (`findLiveBom`) rather than of
   * the recipe array beside it — see `onPickItem`.
   */
  const finishedPicker = useItemPicker(
    useMemo(() => (bom ? [bom.finishedItemId] : []), [bom]),
    { type: 'product' },
  );

  useEffect(() => {
    if (selectedId === null && firstBom) setSelectedId(firstBom.id);
  }, [firstBom, selectedId]);

  const versionsForItem = bom?.versions ?? [];

  const onPickItem = async (itemId: string) => {
    setError(null);
    if (itemId === '') return;
    try {
      /*
       * ASKED OF THE SERVER, not of the list on screen. Searching the loaded
       * recipes for this item is what tied MFG-01 to an unpaged list: a
       * recipe past the cut reads as "no recipe", and §15 would then open a
       * second Draft v1 for an item that already has a live one.
       */
      const existing = await findLiveBom(itemId);
      if (existing) {
        setSelectedId(existing.id);
        return;
      }
    } catch (caught) {
      setError(localizeServerMessage(t, refusalMessage(caught), caught));
      return;
    }
    if (!canCreate || units === undefined || units.length === 0) return;
    try {
      // §15: "a finished item with no recipe opens a blank Draft version v1".
      const created = await create.mutateAsync({ finishedItemId: itemId, uomId: units[0]!.id });
      setSelectedId(created.id);
    } catch (caught) {
      // D46: the cause rides along — a structured refusal
      // (finishedItemMustBeProduct names the item) localizes by key+params;
      // anything else keeps the server's own sentence.
      setError(localizeServerMessage(t, refusalMessage(caught), caught));
    }
  };

  const onSetStatus = async (status: 'draft' | 'active' | 'inactive') => {
    if (bom === undefined) return;
    setError(null);
    try {
      await setStatus.mutateAsync({ bomId: bom.id, status });
      setActivatePrompt(false);
    } catch (caught) {
      const message = refusalMessage(caught);
      // BR-MFG-01-01's own sentence comes back when another version is live.
      // §8.5 answers it with the activate prompt, and NOTHING else: the
      // switch-over is a decision, so the dialog collects it. The RECOGNITION
      // reads the wire's English sentence (stable by D46's compatibility
      // promise); what the strip SHOWS is localized — the cause carries
      // key+params, so the refusal reaches an Arabic reader in Arabic.
      if (status === 'active' && message.includes('active now')) {
        setActivatePrompt(true);
        return;
      }
      setError(localizeServerMessage(t, message, caught));
    }
  };

  return (
    <div className="grid gap-4">
      <ManufacturingTabs />

      {error !== null ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── Region 1: the header card ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            <GitBranch className="size-4 text-muted-foreground" aria-hidden />
            {/* A real heading, not a styled div: this card's title IS the page
                title (§4), and every other screen in the product gives the
                reader one to land on. */}
            <h2 className="me-auto text-lg font-bold">{t('mfg.bomTitle')}</h2>
            {bom !== undefined ? (
              <>
                {/* Field 4: the version chip is the ONLY way into history. */}
                <button
                  type="button"
                  onClick={() => setHistoryOpen(true)}
                  className="flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-xs hover:bg-muted"
                >
                  <History className="size-3" aria-hidden />
                  {bom.label}
                </button>
                <span className="text-sm font-normal text-muted-foreground">
                  {t(`mfg.bomStatus.${bom.status}`)}
                </span>
              </>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="mfg-finished">{t('mfg.finishedItem')}</Label>
              <Typeahead
                id="mfg-finished"
                label={t('mfg.finishedItem')}
                value={bom?.finishedItemId ?? null}
                onChange={(id) => void onPickItem(id ?? '')}
                options={finishedPicker.options}
                selected={finishedPicker.optionFor(bom?.finishedItemId)}
                query={finishedPicker.query}
                onQueryChange={finishedPicker.setQuery}
                placeholder={t('common.select')}
                emptyLabel={t('picker.noMatchingItem')}
                loading={finishedPicker.loading}
              />
            </div>
            <div className="grid gap-1.5">
              {/* Field 2 (owner ruling R6): a PICKER, never free text. */}
              <Label htmlFor="mfg-uom">{t('mfg.uom')}</Label>
              {/* GEN-51 (D26): a unit is one of the four simple records. This
                  field WRITES ON CHANGE rather than on a Save button, so the
                  sentinel had to be intercepted before the mutation — which is
                  exactly what `QuickCreateSelect` guarantees: the create row
                  never reaches `onChange`, so no BOM is ever patched with it. */}
              <QuickCreateSelect
                entity="unit"
                id="mfg-uom"
                value={bom?.uomId ?? ''}
                disabled={bom === undefined || !bom.isEditable || !canEdit}
                onChange={(next) => {
                  if (bom === undefined) return;
                  void update.mutateAsync({ bomId: bom.id, body: { uomId: next } });
                }}
              >
                {(units ?? []).map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </QuickCreateSelect>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mfg-effective">{t('mfg.effectiveDate')}</Label>
              <Input
                id="mfg-effective"
                type="date"
                value={bom?.effectiveDate ?? ''}
                disabled={bom === undefined || !bom.isEditable || !canEdit}
                onChange={(event) => {
                  if (bom === undefined) return;
                  void update.mutateAsync({
                    bomId: bom.id,
                    body: { effectiveDate: event.target.value === '' ? null : event.target.value },
                  });
                }}
              />
            </div>
            {bom !== undefined && bom.status === 'draft' ? (
              <div className="flex items-end">
                <Gated permission="manufacturing.delete" reasonKey="perm.cannot.deleteBoms">
                  <Button variant="outline" onClick={() => setDeleting(true)}>
                    <Trash2 aria-hidden /> {t('common.delete')}
                  </Button>
                </Gated>
                {/* THIS DELETED A BILL OF MATERIALS WITH NO QUESTION AT ALL.
                    GEN-58: "Delete must always ask." Having no
                    `*.confirmDelete` key is what hid it from the key-based
                    census — the sites that ask nothing are invisible to an
                    instrument that enumerates what they say. The
                    `status === 'draft'` guard above already satisfied GEN-58's
                    second clause; only the asking was missing. */}
                <DeleteConfirmBox
                  open={deleting}
                  detail={t('mfg.confirmDelete')}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() => {
                    setDeleting(false);
                    void remove.mutateAsync(bom.id).then(() => setSelectedId(null));
                  }}
                  busy={remove.isPending}
                />
              </div>
            ) : null}
          </div>

          {/* The status control: three buttons and one helper line (§4). */}
          <div className="grid gap-1.5">
            <Label>{t('mfg.status')}</Label>
            <div className="flex flex-wrap gap-2">
              {(['draft', 'active', 'inactive'] as const).map((status) => (
                <Gated key={status} permission="manufacturing.edit" reasonKey="perm.cannot.editBoms">
                  <Button
                    type="button"
                    variant={bom?.status === status ? 'default' : 'outline'}
                    disabled={bom === undefined}
                    onClick={() => void onSetStatus(status)}
                  >
                    {t(`mfg.bomStatus.${status}`)}
                  </Button>
                </Gated>
              ))}
            </div>
            {/* Field 6: "It states the single most important rule on the screen." */}
            <p className="text-xs text-muted-foreground">{t('mfg.oneActiveNote')}</p>
          </div>
        </CardContent>
      </Card>

      {isPending ? <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy /> : null}

      {bom !== undefined ? (
        <>
          {/* ── Region 2: the tab bar. Components opens first (§8.1). ────── */}
          <nav className="flex gap-1 border-b pb-px" aria-label={t('mfg.bomTitle')}>
            {(['components', 'routing'] as const).map((entry) => (
              <button
                key={entry}
                type="button"
                onClick={() => setTab(entry)}
                className={cn(
                  'rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground',
                  tab === entry && 'border-b-2 border-primary text-foreground',
                )}
              >
                {t(entry === 'components' ? 'mfg.tabComponents' : 'mfg.tabRouting')}
              </button>
            ))}
          </nav>

          {tab === 'components' ? (
            <ComponentList bom={bom} canEdit={canEdit} onWhereUsed={setWhereUsedItem} />
          ) : (
            <RoutingList bom={bom} canEdit={canEdit} />
          )}

          {/* ── Region 5: the cost panel, shown under EITHER tab (§4). ──── */}
          <CostPanel bom={bom} canEdit={canEdit} />
        </>
      ) : null}

      {historyOpen && bom !== undefined ? (
        <Modal title={t('mfg.versionHistory')} onClose={() => setHistoryOpen(false)}>
          {/* §8.3: "There is no Compare, no Restore, no Duplicate, no per-row
              menu, and the rows are not clickable. This modal is a ledger you
              look at." */}
          <ul className="grid gap-2">
            {versionsForItem.map((version) => (
              <li key={version.id} className="rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  <Ltr value={version.label} className="w-10 font-mono text-sm font-semibold" />
                  <span className="me-auto text-sm">{version.changeNote ?? '—'}</span>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      version.status === 'active'
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : version.status === 'draft'
                          ? 'text-amber-700 dark:text-amber-400'
                          : 'text-muted-foreground',
                    )}
                  >
                    {t(`mfg.bomStatus.${version.status}`)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <Ltr value={version.effectiveDate ?? '—'} />
                </p>
              </li>
            ))}
          </ul>
        </Modal>
      ) : null}

      {whereUsedItem !== null ? (
        <WhereUsedModal item={whereUsedItem} onClose={() => setWhereUsedItem(null)} />
      ) : null}

      {activatePrompt && bom !== undefined ? (
        /**
         * §8.5. The amber icon says switch-over rather than deletion, and the
         * dialog CANNOT be dismissed by clicking the background — "you must
         * choose one of the two buttons. That is deliberate, and it must be
         * built that way."
         */
        <Modal
          title={localizeServerMessage(t, MFG_MESSAGES.activatePromptTitle)}
          onClose={() => setActivatePrompt(false)}
          dismissible={false}
          className="max-w-md"
        >
          <p className="text-sm text-muted-foreground">
            {localizeServerMessage(t, MFG_MESSAGES.activatePromptBody)}
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => setActivatePrompt(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                setError(null);
                void setStatus
                  .mutateAsync({ bomId: bom.id, status: 'active', deactivateIncumbent: true })
                  .then(() => setActivatePrompt(false))
                  .catch((caught: unknown) =>
                    setError(localizeServerMessage(t, refusalMessage(caught), caught)),
                  );
              }}
            >
              {t('mfg.deactivateAndActivate')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** §5.2 — the Component list, in the design's exact column order. */
function ComponentList({
  bom,
  canEdit,
  onWhereUsed,
}: {
  bom: BomResponse;
  canEdit: boolean;
  onWhereUsed: (item: { id: string; name: string }) => void;
}) {
  const { t } = useTranslation();
  const { data: units } = useUnits();
  const { update } = useBomMutations();
  /*
   * GEN-50 — the parts this recipe may take, searched. MFG-01 §5.2 describes
   * the prototype's control as "a dropdown of the item catalogue, not a
   * type-ahead search"; GEN-50 is the general rule that a Pick-from-list
   * field holding more than ten records puts a SEARCH BOX INSIDE its
   * dropdown, which is exactly this control — a panel of items with a search
   * box above them, not a free-text field. At 50,000 items the prototype's
   * literal shape cannot be built.
   *
   * The three narrowings are the server's: no service (BR-MD-03-07's half
   * that applies to a recipe line), never the finished item itself
   * (BR-MFG-01-04), and never a part already on this recipe — the last stays
   * in the browser because it is about THIS document, not about the catalogue.
   */
  const componentPicker = useItemPicker(
    useMemo(() => bom.components.map((component) => component.itemId), [bom.components]),
    { status: 'active', kitComponent: true, excludeId: bom.finishedItemId },
  );
  const editable = canEdit && bom.isEditable;

  const write = (
    lines: {
      itemId: string;
      qty: string;
      unitId: string;
      scrapPct?: string;
      isOptional?: boolean;
    }[],
  ) => update.mutateAsync({ bomId: bom.id, body: { components: lines } });

  /**
   * ── The list this screen SENDS, kept locally, and why it must be ──────────
   *
   * `update` replaces the whole component list, so every edit posts all the
   * lines. Building that payload from the server's copy on each keystroke
   * loses edits: type a quantity, tab straight to Scrap %, and the scrap write
   * is assembled from props that have not caught up with the quantity yet — so
   * it sends the OLD quantity and silently undoes what was just typed. The
   * journey caught it doing exactly that, reverting 4 back to 1.
   *
   * Holding the edited list here fixes it at the source: each change updates
   * the draft and sends the draft, so the second write carries the first one.
   * The server stays the authority on everything DERIVED — Qty +scrap, unit
   * cost, line cost and the whole costing panel are read from `bom` below and
   * never computed here (BR-MFG-01-12).
   *
   * ── IT IS SEEDED ONCE PER DOCUMENT, AND THIS IS THE LOAD-BEARING PART ─────
   *
   * It used to be re-seeded on `bom.updatedAt` and on any new identity of
   * `bom.components` — "so a write landing from elsewhere does not leave a
   * stale draft behind" — and that is what lost the edits this comment above
   * claims to have fixed. Every write's own response is a new
   * `bom.components`, so the draft was re-seeded FROM THE SERVER in the middle
   * of a burst of typing. An edit that had been typed but whose write was
   * still queued was not in that response, so the next payload was built
   * without it, and the next write then overwrote it.
   *
   * MEASURED through the real HTTP stack, five rounds of the journey's own
   * sequence with BOM reads held at 4,500 ms: **three of five recipes ended
   * WRONG ON THE SERVER** — `A:0 LED:3`, `A:2 LED:0`, and `A:0 LED:0` giving
   * material costs of 7.41, 7.43 and **7.35**. That last figure is the one
   * `manufacturing.spec.ts` reported and could not explain, and it is not a
   * display fault: the recipe in the database carried a scrap allowance
   * nobody typed. With the re-seed reduced to the document's identity: 0 of 5.
   *
   * So the rule is: THE DRAFT IS THE USER'S INTENT and the server does not get
   * to overwrite it while they are still typing. It is seeded when the
   * document being edited CHANGES — a different version chosen, a different
   * recipe opened — and not when the document merely comes back from a write.
   * Everything DERIVED is still read from `bom` below and never computed here
   * (BR-MFG-01-12): Qty +scrap, unit cost, line cost, the costing panel.
   *
   * What this gives up, deliberately: a change made in ANOTHER tab no longer
   * rewrites this editor's pending list. It never safely could — that is two
   * people editing one draft, which needs optimistic concurrency on the route
   * and a refusal this screen can show, and is written up on `useBomMutations`.
   */
  const fromServer = useMemo(
    () =>
      bom.components.map((component) => ({
        itemId: component.itemId,
        qty: component.qty,
        unitId: component.unitId,
        scrapPct: component.scrapPct,
        isOptional: component.isOptional,
      })),
    [bom.components],
  );
  const [draft, setDraft] = useState(fromServer);
  // Read through a ref so the effect can depend on the DOCUMENT alone: making
  // `fromServer` a dependency is precisely the re-seed that lost the edits.
  const latestFromServer = useRef(fromServer);
  latestFromServer.current = fromServer;
  useEffect(() => setDraft(latestFromServer.current), [bom.id]);

  const current = draft;
  const commit = (lines: typeof draft) => {
    setDraft(lines);
    return write(lines);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('mfg.componentList')}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('mfg.colComponent')}</TableHead>
              <TableHead className="text-end">{t('mfg.colQty')}</TableHead>
              <TableHead>{t('mfg.colUom')}</TableHead>
              <TableHead className="text-end">{t('mfg.colScrap')}</TableHead>
              <TableHead className="text-end">{t('mfg.colQtyScrap')}</TableHead>
              <TableHead className="text-end">{t('mfg.colUnitCost')}</TableHead>
              <TableHead className="text-end">{t('mfg.colLineCost')}</TableHead>
              <TableHead>{t('mfg.colOptional')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {bom.components.map((component, index) => (
              /*
                KEYED BY THE ITEM, NOT BY THE LINE'S id, and that is a fix
                rather than a preference. `replaceComponents` DELETES every row
                and re-inserts it on every write, so `component.id` is a
                different value after each one — the whole grid unmounts and
                remounts, and these boxes are UNCONTROLLED (`defaultValue`). A
                remount landing between a value being typed and the field being
                left throws the typed value away with the old DOM node, and the
                blur that follows reads the FRESH box, which holds the server's
                figure. Measured through the real stack with reads held at
                4,500 ms: a component's scrap silently reverting to 0 in 2 of 6
                recipes, with the screen showing the reverted value quite
                honestly, because it really had been written.

                `BR-MFG-01-05` is what makes the item id a legal key: "an item
                must not appear twice on one recipe", enforced on the server
                with the message naming the earlier line. So it is unique per
                row, and — unlike the line id — it is the same before and after
                a write, which is exactly what React needs to keep the box a
                user is typing into alive.
              */
              <TableRow key={component.itemId} className="odd:bg-muted/30">
                <TableCell>
                  <div className="text-sm font-medium">{component.itemName}</div>
                  <Ltr value={component.itemCode} className="font-mono text-xs text-muted-foreground" />
                  {/* Field 8: the blue sub-assembly label — a LINK here, per
                      §5.2's [decided at build time]. */}
                  {component.isSubAssembly ? (
                    <div className="mt-0.5 text-xs font-medium text-blue-600 dark:text-blue-400">
                      {t('mfg.subAssembly')}
                    </div>
                  ) : null}
                </TableCell>
                {/*
                  * `N-4`'s DECIMALS, and only those. A frozen version's recipe
                  * quantity can never be typed into again (`BR-MFG-01-18`: only
                  * a Draft is editable), so there it is a display and takes the
                  * decimals `component.unitId` allows — a component called for
                  * in 1.020 kg had been printing "1.02".
                  *
                  * NO SYMBOL, and that is argued rather than skipped: this
                  * table carries a PER-ROW UoM column in the very next cell,
                  * which is INV-05 §5.2's exception — the unit IS written next
                  * to the number — and repeating it here would be the noise
                  * `N-4` does not ask for.
                  */}
                <TableCell className="text-end">
                  {editable ? (
                    <Input
                      aria-label={`${t('mfg.colQty')} ${component.itemName}`}
                      className="w-24 text-end font-mono"
                      defaultValue={trimQty(component.qty)}
                      onBlur={(event) => {
                        const next = [...current];
                        next[index] = { ...next[index]!, qty: event.target.value };
                        void commit(next);
                      }}
                    />
                  ) : (
                    <Ltr
                      value={formatQty(component.qty, component.unitPrecision)}
                      className="font-mono tabular-nums"
                    />
                  )}
                </TableCell>
                {/* INV-05 §5.2's per-row unit column, and the reason the two
                    figures either side of it print bare. */}
                <TableCell className="text-sm">{component.unitSymbol}</TableCell>
                <TableCell className="text-end">
                  <Input
                    aria-label={`${t('mfg.colScrap')} ${component.itemName}`}
                    className="w-20 text-end font-mono"
                    defaultValue={trimQty(component.scrapPct)}
                    disabled={!editable}
                    onBlur={(event) => {
                      const next = [...current];
                      next[index] = { ...next[index]!, scrapPct: event.target.value };
                      void commit(next);
                    }}
                  />
                </TableCell>
                {/*
                  * Field 12, read-only: qty × (1 + scrap ÷ 100), 3 dp.
                  *
                  * THREE DECIMALS EVEN IN A 0-DECIMAL UNIT, and that is not an
                  * `N-4` miss. This is a per-unit scrap ALLOWANCE, not a
                  * countable quantity of things: 4 screws at 5 % scrap is
                  * 4.200, and `BR-MFG-02-03` is what turns it into a whole
                  * number — ceil(allowance × batch). Rounding it to the unit's
                  * decimals here would print "4" and erase the allowance this
                  * column exists to show, changing what the next rule computes.
                  * PART 18: "where a formula and this rule disagree, the
                  * formula wins, because it is transcribed from the product."
                  * The unit itself is in the UoM column on this row.
                  */}
                <TableCell className="text-end font-mono text-sm">{component.qtyWithScrap}</TableCell>
                <TableCell className="text-end">
                  <Amount value={component.unitCost} className="text-sm" />
                </TableCell>
                <TableCell className="text-end">
                  <Amount value={component.lineCost} className="text-sm font-semibold" />
                </TableCell>
                <TableCell>
                  {/* Field 15: the default is applied VISIBLY — the switch is
                      on the row, not assumed behind it (§9). */}
                  <input
                    type="checkbox"
                    aria-label={`${t('mfg.colOptional')} ${component.itemName}`}
                    checked={component.isOptional}
                    disabled={!editable}
                    onChange={(event) => {
                      const next = [...current];
                      next[index] = { ...next[index]!, isOptional: event.target.checked };
                      void commit(next);
                    }}
                  />
                </TableCell>
                <TableCell className="flex gap-1">
                  {/* §6: Where used is open to anyone who may open the screen —
                      "it reads and changes nothing". */}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`${t('mfg.whereUsed')} ${component.itemName}`}
                    onClick={() => onWhereUsed({ id: component.itemId, name: component.itemName })}
                  >
                    <ScanSearch aria-hidden />
                  </Button>
                  {editable ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${t('common.delete')} ${component.itemName}`}
                      onClick={() => void commit(current.filter((_, at) => at !== index))}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* §5.2: "There is no totals row in the component table… That is
            deliberate and must be built that way." */}
        {editable ? (
          <div className="mt-3">
            <ItemCell
              label={t('mfg.addComponent')}
              picker={{
                ...componentPicker,
                // "An item must not appear twice on one recipe" (§5.2) — a
                // fact about this document, so it narrows the panel here.
                options: componentPicker.options.filter(
                  (option) =>
                    !bom.components.some((component) => component.itemId === option.id),
                ),
              }}
              value=""
              onPick={(itemId) => {
                if (itemId === '') return;
                void commit([
                  ...current,
                  {
                    itemId,
                    qty: '1',
                    unitId: units?.[0]?.id ?? '',
                    scrapPct: '0',
                    isOptional: false,
                  },
                ]);
              }}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** §5.3 — the routing. Time, workstation and order are ALL it holds. */
function RoutingList({ bom, canEdit }: { bom: BomResponse; canEdit: boolean }) {
  const { t } = useTranslation();
  const { update } = useBomMutations();
  const editable = canEdit && bom.isEditable;

  // The same held draft the component list keeps, for the same reason: a
  // routing edit replaces the whole list, so renaming a step and then typing
  // its minutes would send the OLD name back if the payload were rebuilt from
  // props each time. Reordering makes it sharper still — BR-MFG-01-10 says a
  // move "must renumber every operation and must change nothing else", and
  // rebuilding from stale props is precisely how it would change something.
  const fromServer = useMemo(
    () =>
      bom.operations.map((operation) => ({
        name: operation.name,
        minutes: operation.minutes,
        workstation: operation.workstation,
      })),
    [bom.operations],
  );
  /*
   * SEEDED PER DOCUMENT, for the reason written out on `ComponentList`, and
   * this list had the identical defect: `replaceOperations` deletes and
   * re-inserts every step, so every write's response is a new `bom.operations`
   * and the draft was re-seeded from it mid-typing. §5.3 is typed exactly the
   * way that bites — name, then minutes, then workstation, one blur after
   * another — so a response landing between two of them reset the draft to a
   * state without the edit still queued, and the next write carried it away.
   * Not the failure the journey caught, and fixed with it because it is the
   * same defect on the same screen.
   */
  const [draft, setDraft] = useState(fromServer);
  const latestFromServer = useRef(fromServer);
  latestFromServer.current = fromServer;
  useEffect(() => setDraft(latestFromServer.current), [bom.id]);

  const current = draft;
  const write = (operations: typeof draft) => {
    setDraft(operations);
    return update.mutateAsync({ bomId: bom.id, body: { operations } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="me-auto">{t('mfg.routing')}</span>
          {/* Field 20: the running total, in the card header — not a table row. */}
          <span className="text-sm font-normal text-muted-foreground">
            {t('mfg.totalTime', { minutes: bom.totalMinutes })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {bom.operations.length === 0 ? (
          // §8.2: "a recipe with no operations has zero labor cost. That is
          // legal — a product may be pure assembly with no timed steps."
          <p className="py-6 text-center text-sm text-muted-foreground">{t('mfg.noRouting')}</p>
        ) : null}
        {bom.operations.map((operation, index) => (
          /*
            KEYED BY THE SEQUENCE, not the row's id, and for the reason spelt
            out on the component grid: `replaceOperations` re-inserts every
            step on every write, so `operation.id` is a different value
            afterwards, the whole list remounts, and these UNCONTROLLED boxes
            lose whatever was typed but not yet left.

            `seq` is the legal key here because `BR-MFG-01-10` makes it the
            row's POSITION — "a move must renumber every operation and must
            change nothing else" — so it is unique, it is stable across an
            ordinary edit, and on a REORDER the rows really have moved and
            remounting them is correct rather than a loss.
          */
          <div key={operation.seq} className="grid items-end gap-2 rounded-lg border p-3 sm:grid-cols-[2rem_1fr_6rem_1fr_auto]">
            {/* Field 16 / BR-MFG-01-10: the sequence IS the row's position. */}
            <span className="font-mono text-sm text-muted-foreground">{operation.seq}</span>
            <Input
              aria-label={t('mfg.operation')}
              defaultValue={operation.name}
              disabled={!editable}
              onBlur={(event) => {
                const next = [...current];
                next[index] = { ...next[index]!, name: event.target.value };
                void write(next);
              }}
            />
            <Input
              aria-label={t('mfg.minutes')}
              type="number"
              className="text-end font-mono"
              defaultValue={operation.minutes}
              disabled={!editable}
              onBlur={(event) => {
                const next = [...current];
                next[index] = { ...next[index]!, minutes: Number(event.target.value) };
                void write(next);
              }}
            />
            <div className="grid gap-1">
              <Input
                aria-label={t('mfg.workstation')}
                defaultValue={operation.workstation ?? ''}
                disabled={!editable}
                onBlur={(event) => {
                  const next = [...current];
                  next[index] = { ...next[index]!, workstation: event.target.value };
                  void write(next);
                }}
              />
              {/* BR-MFG-01-11 — a WARNING, never a block. §15 calls this the
                  most likely real-world failure on the screen. */}
              {operation.isLonelyWorkstation && operation.workstation !== null ? (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {/* PARAMETERISED, so the exact-match map cannot hold it: the
                      sentence names the workstation. Addressed as an ICU key
                      here, where the argument is known. */}
                  {t('mfg.lonelyWorkstation', {
                    workstation: visibleWorkstation(operation.workstation),
                  })}
                </p>
              ) : null}
            </div>
            {editable ? (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('mfg.moveUp')}
                  disabled={index === 0}
                  onClick={() => {
                    const next = [...current];
                    const [moved] = next.splice(index, 1);
                    next.splice(index - 1, 0, moved!);
                    void write(next);
                  }}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('mfg.moveDown')}
                  disabled={index === bom.operations.length - 1}
                  onClick={() => {
                    const next = [...current];
                    const [moved] = next.splice(index, 1);
                    next.splice(index + 1, 0, moved!);
                    void write(next);
                  }}
                >
                  ↓
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.delete')}
                  onClick={() => void write(current.filter((_, at) => at !== index))}
                >
                  <Trash2 aria-hidden />
                </Button>
              </div>
            ) : null}
          </div>
        ))}
        {editable ? (
          <Button
            variant="outline"
            onClick={() =>
              void write([...current, { name: t('mfg.newOperation'), minutes: 1, workstation: null }])
            }
          >
            <Plus aria-hidden /> {t('mfg.addOperation')}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** §5.4 — read-only apart from ONE field (BR-MFG-01-12). */
function CostPanel({ bom, canEdit }: { bom: BomResponse; canEdit: boolean }) {
  const { t } = useTranslation();
  const { update } = useBomMutations();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="me-auto">{t('mfg.costPerUnit')}</span>
          <span className="text-xs font-normal text-muted-foreground">{t('mfg.autoCalculated')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-4">
        <div>
          <p className="text-xs text-muted-foreground">{t('mfg.materialCost')}</p>
          <Amount value={bom.cost.materialCost} className="text-lg font-semibold" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('mfg.laborCost')}</p>
          <Amount value={bom.cost.laborCost} className="text-lg font-semibold" />
          {/* Field 23: one company-wide rate, set in Settings. */}
          <p className="text-xs text-muted-foreground">
            @ {bom.cost.laborRatePerMinute}
            {t('mfg.perMinute')}
          </p>
        </div>
        <div className="grid gap-1">
          <Label htmlFor="mfg-overhead" className="text-xs text-muted-foreground">
            {t('mfg.overhead')}
          </Label>
          <div className="flex items-center gap-2">
            {/* Field 24: "The only thing you can type in this panel." It stays
                editable on an ACTIVE version — BR-MFG-01-13's one exception. */}
            <Input
              id="mfg-overhead"
              className="w-20 text-end font-mono"
              defaultValue={trimQty(bom.cost.overheadPct)}
              disabled={!canEdit || bom.status === 'inactive'}
              onBlur={(event) =>
                void update.mutateAsync({
                  bomId: bom.id,
                  body: { overheadPct: event.target.value },
                })
              }
            />
            <span className="text-sm text-muted-foreground">{t('unit.percentSign')}</span>
            <Amount value={bom.cost.overhead} className="text-sm" />
          </div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('mfg.totalPerUnit')}</p>
          <Amount value={bom.cost.totalPerUnit} className="text-2xl font-bold text-primary" />
        </div>
      </CardContent>
    </Card>
  );
}

/** §8.4 — upward only. "You cannot look downward into a sub-assembly here." */
function WhereUsedModal({
  item,
  onClose,
}: {
  item: { id: string; name: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: rows, isPending } = useWhereUsed(item.id);

  return (
    <Modal title={t('mfg.whereUsed')} onClose={onClose} className="max-w-lg">
      <p className="mb-3 text-sm text-muted-foreground">{item.name}</p>
      {isPending ? (
        <div className="h-20 animate-pulse rounded-md bg-muted" aria-busy />
      ) : (rows ?? []).length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {localizeServerMessage(t, MFG_MESSAGES.notUsedAnywhere)}
        </p>
      ) : (
        <ul className="grid gap-2">
          {(rows ?? []).map((row) => (
            <li key={row.bomVersionId} className="flex items-center gap-3 rounded-lg border p-3">
              <GitBranch className="size-4 text-muted-foreground" aria-hidden />
              <div className="me-auto">
                <div className="text-sm font-medium">{row.parentName}</div>
                <Ltr value={row.finishedSku} className="font-mono text-xs text-muted-foreground" />
              </div>
              <Ltr value={row.label} className="font-mono text-xs" />
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
